import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { Spinner, Alert, Table, Pagination, Button } from 'react-bootstrap';
import './UserProfile.css';

const UserProfile = () => {
  const { user, loading } = useContext(AuthContext);
  const [cryptoAddress, setCryptoAddress] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [utxos, setUtxos] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const navigate = useNavigate();

  useEffect(() => {
    if (!user && !loading) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    setError('');
    setIsLoading(true);

    try {
      const authHeader = { Authorization: `Bearer ${user.token}` };

      const addressResponse = await axios.get(`/api/transactions/user/${user.id}/address`, {
        headers: authHeader,
      });
      setCryptoAddress(addressResponse.data.address);

      await fetchBalance(addressResponse.data.address);
      await fetchUtxos(addressResponse.data.address);

      const transactionsResponse = await axios.get(`/api/transactions/user/${user.id}/transactions`, {
        headers: authHeader,
      });
      setTransactions(transactionsResponse.data.transactions || []);
    } catch (err) {
      setError('Error fetching user data.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalance = async (address) => {
    try {
      const response = await axios.get(`/api/transactions/crypto/balance/${address}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setBalance(response.data.balance);
    } catch (err) {
      setError('Error fetching balance.');
    }
  };

  const fetchUtxos = async (address) => {
    try {
      const response = await axios.get(`/api/transactions/utxos/${address}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setUtxos(response.data.utxos || []);
    } catch (err) {
      setError('Error fetching UTXOs.');
    }
  };

  const paginate = (items) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const renderPagination = (items) => {
    const totalPages = Math.ceil(items.length / itemsPerPage);

    if (totalPages <= 1) return null;

    const handleNext = () => {
      if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePrev = () => {
      if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
          className="UserProfile-pagination-item"
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="UserProfile-pagination">
        <Pagination.Prev onClick={handlePrev} disabled={currentPage === 1} />
        {pageNumbers}
        <Pagination.Next onClick={handleNext} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  const requestNewAddress = async () => {
    setError('');
    setMessage('');
    setIsLoading(true);
    try {
      const response = await axios.post(`/api/transactions/user/${user.id}/address`, {}, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setCryptoAddress(response.data.address);
      setMessage('Address successfully generated.');
    } catch (err) {
      setError('Error generating new address.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="UserProfile-loader">
        <Spinner animation="border" role="status" className="UserProfile-spinner">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="UserProfile-container">
      <h2 className="UserProfile-title">User Profile</h2>

      {error && <Alert variant="danger" className="UserProfile-alert UserProfile-alert-error">{error}</Alert>}
      {message && <Alert variant="success" className="UserProfile-alert UserProfile-alert-success">{message}</Alert>}

      <div className="UserProfile-balance">
        <h3 className="UserProfile-subtitle">Cryptocurrency Address</h3>
        {cryptoAddress ? (
          <div>
            <p>
              <strong>Your cryptocurrency address:</strong> {cryptoAddress}
            </p>
            <p>
              <strong>Balance:</strong> {balance.toFixed(8)} FUEC
            </p>
            <Alert variant="info" className="UserProfile-info">
            This wallet does not support withdrawals. All coins sent to this wallet are intended to be spent and burned via the services.
            </Alert>
          </div>
        ) : (
          <Button onClick={requestNewAddress} className="UserProfile-button">
            Request New Address
          </Button>
        )}
      </div>

      <div>
        <h3 className="UserProfile-subtitle">Address UTXOs</h3>
        {utxos.length === 0 ? (
          <p>No UTXOs available.</p>
        ) : (
          <>
            <Table striped bordered hover responsive className="UserProfile-table">
              <thead>
                <tr>
                  <th>TXID</th>
                  <th>Vout</th>
                  <th>Amount</th>
                  <th>Available</th>
                </tr>
              </thead>
              <tbody>
                {paginate(utxos).map((utxo) => (
                  <tr key={`${utxo.txid}-${utxo.vout}`}>
                    <td>{utxo.txid}</td>
                    <td>{utxo.vout}</td>
                    <td>{utxo.amount}</td>
                    <td>{utxo.spent ? 'No' : 'Yes'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {renderPagination(utxos)}
          </>
        )}
      </div>

      <div>
        <h3 className="UserProfile-subtitle">Transaction History</h3>
        {transactions.length === 0 ? (
          <p>No transactions found.</p>
        ) : (
          <>
            <Table striped bordered hover responsive className="UserProfile-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Address</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {paginate(transactions).map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.transaction_id || 'Pending'}</td>
                    <td>{transaction.crypto_address}</td>
                    <td>{transaction.amount}</td>
                    <td>{transaction.status}</td>
                    <td>{new Date(transaction.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {renderPagination(transactions)}
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
